import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import bulkAutoSendService from "../../../../services/bulkAutoSendService";
import { BULK_SEND_BATCH_SIZE } from "../../../../config/bulkAutoSendConfig";
import ProductSelectionStep from "./ProductSelectionStep";
import ScrollToBottomFab from "./ScrollToBottomFab";

const LOG_COLORS = {
  info: "text.primary",
  success: "success.main",
  warn: "warning.main",
  error: "error.main",
};

const ALL_CATEGORIES_OPTION = { id: null, name: "Tüm kategoriler" };

function chunkIds(ids, size) {
  const chunks = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

function buildSendStartMessage(placeName, productCount, stepCount) {
  if (stepCount <= 1) {
    return `${placeName} pazaryerine ${productCount} ürün gönderiliyor…`;
  }
  return `${placeName} pazaryerine ${productCount} ürün gönderiliyor (${stepCount} adımda)…`;
}

function buildSendStepSuccessMessage(stepIndex, stepCount, productCount) {
  if (stepCount <= 1) {
    return `${productCount} ürün başarıyla gönderildi.`;
  }
  return `${stepIndex}. adım tamamlandı: ${productCount} ürün gönderildi.`;
}

function buildSendStepErrorMessage(stepIndex, stepCount, errorMessage) {
  if (stepCount <= 1) {
    return `Gönderim sırasında sorun oluştu: ${errorMessage}`;
  }
  return `${stepIndex}. adımda sorun oluştu: ${errorMessage}`;
}

function buildSendFinishMessage(sentCount, errorCount) {
  if (sentCount > 0 && errorCount === 0) {
    return `İşlem tamamlandı. ${sentCount} ürün başarıyla gönderildi.`;
  }
  if (sentCount > 0 && errorCount > 0) {
    return `İşlem tamamlandı. ${sentCount} ürün gönderildi; ${errorCount} adımda sorun oluştu.`;
  }
  if (errorCount > 0) {
    return "Ürünler gönderilemedi. Lütfen hata mesajlarına bakın.";
  }
  return "Gönderim yapılmadı.";
}

const BulkAutoSendPanel = ({
  userId,
  token,
  allPlaces,
  marketpleacesAccounts,
  ecommerceAccounts,
  categories = [],
  onCategorySearch,
}) => {
  const listAnchorRef = useRef(null);
  const [autoStep, setAutoStep] = useState(0);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [containsTitle, setContainsTitle] = useState("");
  const [maxTotalCount, setMaxTotalCount] = useState(500);
  const [products, setProducts] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectingAllListed, setSelectingAllListed] = useState(false);
  const [selectAllProgress, setSelectAllProgress] = useState(null);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({
    currentBatch: 0,
    totalBatches: 0,
    sentCount: 0,
    errorCount: 0,
  });
  const [logs, setLogs] = useState([]);

  const categoryOptions = useMemo(
    () => [ALL_CATEGORIES_OPTION, ...categories],
    [categories]
  );

  const appendLog = useCallback((level, message, detail) => {
    setLogs((prev) => [
      ...prev,
      {
        time: new Date().toLocaleTimeString("tr-TR"),
        level,
        message,
        detail: detail || null,
      },
    ]);
  }, []);

  const getPlaceIds = () => {
    let marketPlaceId = null;
    let ecommercePlaceId = null;
    if (selectedPlace?.type === "ecommerce") {
      ecommercePlaceId = selectedPlace.id;
    } else {
      marketPlaceId = selectedPlace?.id;
    }
    return { marketPlaceId, ecommercePlaceId };
  };

  const fetchProducts = async (targetPage = page) => {
    if (!selectedPlace) return;
    setLoadingProducts(true);
    const { marketPlaceId, ecommercePlaceId } = getPlaceIds();
    try {
      const { products: list, listTotal: total } =
        await bulkAutoSendService.fetchProductsPage({
          userId,
          page: targetPage,
          pageSize,
          marketPlaceId,
          ecommercePlaceId,
          categoryId: selectedCategory?.id ?? undefined,
          containsTitle,
          maxTotalCount,
        });
      setListTotal(total);
      setProducts(list ?? []);
      setPage(targetPage);
    } catch (err) {
      toast.error(bulkAutoSendService.formatApiError(err));
      console.error(err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleConfigContinue = async () => {
    if (!selectedPlace) {
      toast.warn("Pazaryeri seçin.");
      return;
    }
    if (!maxTotalCount || maxTotalCount < 1) {
      toast.warn("Geçerli bir ürün adedi girin.");
      return;
    }
    setSelectedProducts([]);
    setLogs([]);
    setPage(1);
    setAutoStep(1);
    await fetchProducts(1);
  };

  useEffect(() => {
    if (autoStep !== 1 || !selectedPlace) return;
    setPage(1);
    fetchProducts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pageSize değişince yeniden listele
  }, [pageSize]);

  const handleSelectAllListed = async () => {
    if (!selectedPlace) return;
    setSelectingAllListed(true);
    setSelectAllProgress({
      percent: 0,
      statusText: "Seçilebilir ürünler taranıyor…",
      selectedCount: 0,
    });
    const { marketPlaceId, ecommercePlaceId } = getPlaceIds();
    try {
      const ids = await bulkAutoSendService.fetchAllSendableProductIds({
        userId,
        pageSize: Number(pageSize) || 10,
        marketPlaceId,
        ecommercePlaceId,
        categoryId: selectedCategory?.id ?? undefined,
        containsTitle,
        maxTotalCount,
        onProgress: setSelectAllProgress,
      });
      setSelectedProducts(ids);
      if (ids.length > 0) {
        toast.success(
          `${ids.length} seçilebilir ürün tüm sayfalardan seçildi.`
        );
      } else {
        toast.warn("Seçilebilir ürün bulunamadı.");
      }
    } catch (err) {
      toast.error(bulkAutoSendService.formatApiError(err));
    } finally {
      setSelectingAllListed(false);
      setSelectAllProgress(null);
    }
  };

  const handleSendSelected = async () => {
    if (!selectedProducts.length) {
      toast.warn("Göndermek için en az bir ürün seçin.");
      return;
    }

    const batches = chunkIds(selectedProducts, BULK_SEND_BATCH_SIZE);
    setSending(true);
    setLogs([]);
    setProgress({
      currentBatch: 0,
      totalBatches: batches.length,
      sentCount: 0,
      errorCount: 0,
    });

    appendLog(
      "info",
      buildSendStartMessage(
        selectedPlace.name,
        selectedProducts.length,
        batches.length
      )
    );

    let sentCount = 0;
    let errorCount = 0;

    for (let i = 0; i < batches.length; i += 1) {
      const batchIds = batches[i];
      setProgress((prev) => ({ ...prev, currentBatch: i + 1 }));

      try {
        const sendResult = await bulkAutoSendService.sendProductIds({
          productIds: batchIds,
          selectedPlace,
          userId,
          token,
          marketpleacesAccounts,
          ecommerceAccounts,
          categoryId: selectedCategory?.id ?? 0,
        });
        sentCount += batchIds.length;
        appendLog(
          "success",
          buildSendStepSuccessMessage(i + 1, batches.length, batchIds.length)
        );
        if (import.meta.env.DEV && sendResult?.requestCount != null) {
          console.debug(
            "[BulkAutoSend] OutWard istek sayısı:",
            sendResult.requestCount
          );
        }
      } catch (err) {
        errorCount += 1;
        const msg = bulkAutoSendService.formatApiError(err);
        console.error("[BulkAutoSend] gönderim hatası:", err);
        appendLog(
          "error",
          buildSendStepErrorMessage(i + 1, batches.length, msg)
        );
      }

      setProgress({
        currentBatch: i + 1,
        totalBatches: batches.length,
        sentCount,
        errorCount,
      });
    }

    appendLog(
      errorCount > 0 ? "warn" : sentCount > 0 ? "success" : "warn",
      buildSendFinishMessage(sentCount, errorCount)
    );

    if (sentCount > 0 && errorCount === 0) {
      toast.success(`${sentCount} ürün gönderildi.`);
    } else if (sentCount > 0 && errorCount > 0) {
      toast.warn(
        `${sentCount} ürün gönderildi; ${errorCount} adımda sorun oluştu. Detaylar aşağıda.`
      );
    } else if (errorCount > 0) {
      toast.error("Ürünler gönderilemedi. Lütfen işlem özetine bakın.");
    } else {
      toast.warn("Gönderim yapılmadı.");
    }

    setSending(false);
  };

  const progressPercent =
    progress.totalBatches > 0
      ? Math.round((progress.currentBatch / progress.totalBatches) * 100)
      : 0;

  return (
    <Stack spacing={3} sx={{ mt: 2 }} ref={listAnchorRef}>
      <Alert severity="info">
        <AlertTitle sx={{ fontWeight: 600 }}>Otomatik gönderim</AlertTitle>
        Bu özellik yalnızca e-ticaret uygulamaları ile kategori özellikleri
        tanımlanmış pazaryerleri için kullanılabilir. Diğer pazaryerlerinde
        lütfen manuel gönderim modunu tercih ediniz.
        <Box component="span" display="block" sx={{ mt: 1.5 }}>
          Önce filtre ve ürün listesini onaylayınız; ardından seçili ürünleri
          gönderiniz. Pazaryerinde kayıtlı veya gönderimi devam eden ürünler
          seçilemez.
        </Box>
      </Alert>

      {autoStep === 0 && (
        <Stack spacing={2}>
          <Autocomplete
            options={allPlaces}
            getOptionLabel={(option) => option.name || ""}
            value={selectedPlace}
            onChange={(_, value) => setSelectedPlace(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pazaryeri veya E-ticaret Platformu"
                variant="outlined"
                fullWidth
              />
            )}
          />

          <Autocomplete
            options={categoryOptions}
            getOptionLabel={(option) => option.name || ""}
            value={selectedCategory ?? ALL_CATEGORIES_OPTION}
            onChange={(_, value) =>
              setSelectedCategory(
                value?.id == null ? null : value
              )
            }
            onInputChange={(_, value, reason) => {
              if (reason === "input" && onCategorySearch) {
                onCategorySearch(value);
              }
            }}
            isOptionEqualToValue={(option, value) =>
              (option?.id ?? null) === (value?.id ?? null)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Kategori (boş = tüm ürünler)"
                variant="outlined"
                fullWidth
              />
            )}
          />

          <TextField
            fullWidth
            label="Başlıkta İçeren Metin"
            variant="outlined"
            value={containsTitle}
            onChange={(e) => setContainsTitle(e.target.value)}
          />

          <TextField
            label="Listelenecek maksimum ürün adedi"
            type="number"
            value={maxTotalCount}
            onChange={(e) =>
              setMaxTotalCount(Math.max(1, parseInt(e.target.value, 10) || 1))
            }
            helperText={`En fazla ${maxTotalCount} ürün listelenir; sayfa boyutunu ürün listesinde seçebilirsiniz`}
            inputProps={{ min: 1, step: 1 }}
            fullWidth
          />

          <Button
            variant="contained"
            size="large"
            disabled={!selectedPlace}
            onClick={handleConfigContinue}
          >
            Devam
          </Button>
        </Stack>
      )}

      {autoStep === 1 && (
        <>
          {loadingProducts ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <ProductSelectionStep
              products={products}
              onBack={() => {
                setAutoStep(0);
                setSelectedProducts([]);
              }}
              onComplete={() => setAutoStep(2)}
              totalCount={listTotal}
              setPage={setPage}
              page={page}
              pageSize={pageSize}
              setPageSize={setPageSize}
              getCategoryProducts={fetchProducts}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
              maxSelectedProducts={maxTotalCount}
              onSelectAllListed={handleSelectAllListed}
              selectingAllListed={selectingAllListed}
              selectAllProgress={selectAllProgress}
            />
          )}
          <ScrollToBottomFab />
        </>
      )}

      {autoStep === 2 && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Gönderim özeti
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Platform: {selectedPlace?.name}
              {selectedCategory?.name
                ? ` · Kategori: ${selectedCategory.name}`
                : " · Tüm kategoriler"}
              {containsTitle ? ` · Başlık: "${containsTitle}"` : ""}
            </Typography>
            <Chip
              sx={{ mt: 1 }}
              label={`${selectedProducts.length} ürün seçildi`}
              color="primary"
              variant="outlined"
            />
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "center" }}
                spacing={2}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Gönderim işlemi
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Seçili ürünler {selectedPlace?.name} platformuna
                    iletilecektir. İşlem süresince bu ekranda kalmanız
                    önerilir.
                  </Typography>
                </Box>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  sx={{
                    width: { xs: "100%", md: "auto" },
                    minWidth: { md: 400 },
                    flexShrink: 0,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setAutoStep(1)}
                    disabled={sending}
                    fullWidth
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      minWidth: { md: 120 },
                    }}
                  >
                    Geri
                  </Button>
                  <Button
                    variant="contained"
                    disabled={sending || selectedProducts.length === 0}
                    onClick={handleSendSelected}
                    fullWidth
                    sx={{
                      py: 1.25,
                      px: 2.5,
                      textTransform: "none",
                      fontWeight: 600,
                      minWidth: { md: 260 },
                    }}
                  >
                    {sending
                      ? "Gönderiliyor…"
                      : "Otomatik gönderimi başlat"}
                  </Button>
                </Stack>
              </Stack>

              {sending && (
                <Box
                  sx={{
                    pt: 2,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {progress.totalBatches <= 1
                        ? "Gönderim devam ediyor…"
                        : `Adım ${progress.currentBatch} / ${progress.totalBatches}`}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                      %{progressPercent}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{ height: 6, borderRadius: 3, mb: 1.5 }}
                  />
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={`Gönderilen ürün: ${progress.sentCount}`}
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`Sorunlu adım: ${progress.errorCount}`}
                      color={progress.errorCount ? "error" : "default"}
                      variant="outlined"
                    />
                  </Stack>
                </Box>
              )}
            </Stack>
          </Paper>

          {logs.length > 0 && (
            <Paper
              variant="outlined"
              sx={{ p: 2, maxHeight: 320, overflow: "auto", bgcolor: "grey.50" }}
            >
              <Typography variant="subtitle2" gutterBottom>
                İşlem özeti
              </Typography>
              {logs.map((entry, index) => (
                <Box key={index} sx={{ mb: 1.25 }}>
                  <Typography
                    variant="body2"
                    display="block"
                    sx={{
                      color: LOG_COLORS[entry.level] || LOG_COLORS.info,
                      fontWeight: entry.level === "error" ? 600 : 400,
                    }}
                  >
                    <Box component="span" sx={{ color: "text.secondary", mr: 1 }}>
                      {entry.time}
                    </Box>
                    {entry.message}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default BulkAutoSendPanel;
